import { inject, Injectable } from "@angular/core";
import ColorScheme from "color-scheme";
import invert from "invert-color";
import { ICompany } from "../../model/custom/ICompany";
import { IPortal } from "../../model/custom/IPortal";
import { ICompanySettings } from "../../model/custom/ISettings";
import { ImageService } from "./image.service";

@Injectable({
	providedIn: "root",
})
export class ThemeService {
	private imageService: ImageService = inject(ImageService);

	public setRandomBackground() {
		const maxNumberOfImages = 12;
		const offset = Math.floor(Math.random() * (maxNumberOfImages - 1 + 1) + 1);

		document.documentElement.style.setProperty(
			"--custom-portal-background-image",
			`url(assets/images/backgrounds/${offset}.jpg)`,
		);
	}
	public setFromSettings(settings: ICompanySettings) {
		if (!settings) {
			return;
		}
		if (settings.CustomTextColor && settings.CustomTextColor.length > 0) {
			document.documentElement.style.setProperty(
				"--custom-text-color",
				settings.CustomTextColor,
			);
		}

		if (settings.CustomBGColor && settings.CustomBGColor.length > 0) {
			const isDarker =
				this.getPerceptualBrightness(settings.CustomBGColor) <
				this.getPerceptualBrightness(settings.CustomTextColor);

			document.documentElement.style.setProperty(
				"--custom-background-color",
				settings.CustomBGColor,
			);
			const invertedCustomColor1Light = this.invertColorLight(
				settings.CustomBGColor,
			);
			const invertedCustomColor1Pale = this.invertColorPale(settings.CustomBGColor);

			if (invertedCustomColor1Light) {
				document.documentElement.style.setProperty(
					"--custom-background-inverted-color",
					invertedCustomColor1Light,
				);
			}
			if (invertedCustomColor1Pale) {
				document.documentElement.style.setProperty(
					"--custom-background-light-color",
					invertedCustomColor1Pale,
				);
			}
		}
	}
	public applyToCompany(companyOrig: ICompany) {
		const company = { ...companyOrig };
		if (company && company.CustomColor1) {
			const invertedCustomColor1Light = this.invertColorLight(company.CustomColor1);
			const invertedCustomColor1Pale = this.invertColorPale(company.CustomColor1);
			if (invertedCustomColor1Light) {
				document.documentElement.style.setProperty(
					"--custom-text-color",
					invertedCustomColor1Light,
				);
				document.documentElement.style.setProperty(
					"--custom-background-color",
					company.CustomColor1,
				);
				document.documentElement.style.setProperty(
					"--custom-background-inverted-color",
					invertedCustomColor1Light,
				);
			}
			if (invertedCustomColor1Pale) {
				document.documentElement.style.setProperty(
					"--custom-background-light-color",
					invertedCustomColor1Pale,
				);
			}
		}
		this.setFromSettings(company.Settings as ICompanySettings);

		return company;
	}

	public applyToPortal(portalOrig: IPortal): IPortal {
		const portal = { ...portalOrig };
		try {
			if (portal.CustomColor1) {
				document.documentElement.style.setProperty(
					"--custom-text-color",
					portal.CustomColor1,
				);

				const { r, g, b } = invert.asRGB(portal.CustomColor1, true);

				document.documentElement.style.setProperty(
					"--custom-portal-background-top-color",
					`rgba(${r}, ${g}, ${b}, 0.75)`,
				);
				document.documentElement.style.setProperty(
					"--custom-portal-background-bottom-color",
					`rgba(${r}, ${g}, ${b}, 0.5)`,
				);
			}
			if (portal.PortalStyleBackgroundImage) {
				portal.PortalStyleBackgroundImage =
					this.imageService.ExpectedImageURLForWidth(
						portal.PortalStyleBackgroundImage,
						window.innerWidth,
					);
				document.documentElement.style.setProperty(
					"--custom-portal-background-image",
					`url('${portal.PortalStyleBackgroundImage}')`,
				);
			} else {
				document.documentElement.style.setProperty(
					"--custom-portal-background-image",
					`url("./assets/images/portal.jpg")`,
				);
			}
		} catch (err) {
			console.log(err);
		}
		return portal;
	}
	private invertColorLight(hex, offset = 0) {
		if (hex.indexOf("#") === 0) {
			hex = hex.slice(1);
		}
		try {
			const scheme = new ColorScheme();
			scheme.from_hex(hex).scheme("triade").variation("light");

			const colors = scheme.colors();
			return `#${colors[colors.length - 1 - offset]}`;
		} catch (err) {
			return "";
		}
	}

	private invertColorPale(hex, offset = 2) {
		if (hex.indexOf("#") === 0) {
			hex = hex.slice(1);
		}
		try {
			const scheme = new ColorScheme();
			scheme.from_hex(hex).scheme("triade").variation("pale");

			const colors = scheme.colors();
			return `#${colors[offset]}`;
		} catch (err) {
			return "";
		}
	}
	private getPerceptualBrightness(hex) {
		if (!hex) {
			return 0;
		}
		if (hex.indexOf("#") === 0) {
			hex = hex.slice(1);
		}
		var r = parseInt(hex.substring(0, 2), 16);
		var g = parseInt(hex.substring(2, 4), 16);
		var b = parseInt(hex.substring(4, 6), 16);

		return r * 2 + g * 3 + b;
	}
}
